import { computed, ref } from 'vue'

export interface ChatMessageToastData {
  chatId: number | string
  senderName: string
  messagePreview: string
  avatar?: string | null
  roomId?: string
}

export interface ChatMessageToastItem extends ChatMessageToastData {
  id: string
}

const toasts = ref<ChatMessageToastItem[]>([])
const timers = new Map<string, ReturnType<typeof setTimeout>>()

const TOAST_LIFE_MS = 8_000

function clearTimer(id: string) {
  const timer = timers.get(id)
  if (!timer) {
    return
  }
  clearTimeout(timer)
  timers.delete(id)
}

function removeToast(id: string) {
  clearTimer(id)
  toasts.value = toasts.value.filter(item => item.id !== id)
}

export function useChatMessageToast() {
  const hide = (id?: string) => {
    if (id) {
      removeToast(id)
      return
    }
    const last = toasts.value[toasts.value.length - 1]
    if (last) {
      removeToast(last.id)
    }
  }

  const hideAll = () => {
    for (const id of [...timers.keys()]) {
      clearTimer(id)
    }
    toasts.value = []
  }

  const show = (data: ChatMessageToastData) => {
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
    toasts.value = [...toasts.value, { ...data, id }]
    timers.set(id, setTimeout(() => removeToast(id), TOAST_LIFE_MS))
  }

  const patchToast = (
    chatId: number | string,
    patch: Partial<ChatMessageToastData>,
  ) => {
    const index = [...toasts.value].reverse().findIndex(item => item.chatId === chatId)
    if (index < 0) {
      return
    }
    const realIndex = toasts.value.length - 1 - index
    const current = toasts.value[realIndex]
    toasts.value = toasts.value.map((item, i) => (
      i === realIndex ? { ...current, ...patch } : item
    ))
  }

  return {
    toasts,
    visible: computed(() => toasts.value.length > 0),
    toast: computed(() => toasts.value[toasts.value.length - 1] ?? null),
    show,
    hide,
    hideAll,
    patchToast,
  }
}
